/**
 * How a loop looks, and which way it runs.
 *
 * A reinforcing loop is a spiral, but nothing about the link types says whether
 * it is one you want. "Syrjäytyminen syvenee → paine kasvaa → syrjäytyminen
 * syvenee" is three cheerful `vahvistaa` edges compounding a disaster, and the
 * sign rule calls it exactly what it calls a virtuous circle. Drawn in the
 * link's own green it read as good news.
 *
 * So direction is about the content, not the arrows. And the arrows cannot even
 * give a useful first guess: a ring of `heikentää` edges dismantling something
 * harmful is a good spiral, and a ring of `vahvistaa` edges compounding a
 * disaster is a bad one. The sign rule says "reinforcing" to both.
 *
 * So nothing guesses. The AI reads the words when it proposes a loop, a person
 * can say so directly, and until one of them has, the loop is drawn as what it
 * is — unjudged — rather than coloured as if somebody had decided.
 *
 * A balancing loop is neither: it holds a thing where it is. It keeps its own
 * colour so it is not read as a mild version of either spiral.
 */

// Each tone's name is impactMapV2.loop.tone.<key> (locales/impactMapV2Strings.js).
export const LOOP_TONES = {
  up: { key: 'up', color: '#E0A82E' },
  // Red, because a spiral running the wrong way is the thing on the map most
  // worth stopping — and it is not the blocker red, because a blocker is
  // something you remove and this is something you break.
  down: { key: 'down', color: '#E0533F' },
  unknown: { key: 'unknown', color: '#8E9BB3' },
};

// A loop on the wall is one of two things: a noidankehä or a hyvä kierre.
// The reinforcing/balancing mechanism (the sign rule over the link types) is
// still stored on the loop, but it no longer decides what the room sees. On
// K8 it called "Verokilpailun syöksykierre" balancing — the links were typed
// by the AI, the rule counted them, and the answer contradicted the name a
// person had already given it. Loop differs from consequence: a consequence
// has a type, a loop has a direction.

/**
 * What the structure alone can say: a balancing loop holds its subject where it
 * is, and that is the whole of it. Whether a reinforcing one is a virtuous
 * circle or a vicious one is not in the graph, so it stays unjudged until
 * somebody judges it.
 */
export function deriveLoopDirection() {
  return 'unknown';
}

export function loopTone(loop) {
  if (!loop) return LOOP_TONES.unknown;
  return LOOP_TONES[loop.direction] || LOOP_TONES.unknown;
}

/** The two a person picks between: impactMapV2.loop.tone.<key> and .hint.<key>. */
export const LOOP_DIRECTIONS = [{ key: 'up' }, { key: 'down' }];

export const loopColor = (loop) => loopTone(loop).color;

/** The short word for the pill on the arc: what the loop is, in the colour it
 *  is drawn in, as a key of impactMapV2.loop.word. 'none' when nobody has
 *  judged the direction yet. */
export function loopWord(loop) {
  if (loop?.direction === 'down' || loop?.direction === 'up') return loop.direction;
  return 'none';
}
